import {
  Badge,
  Box,
  Button,
  Title,
  BackButton,
  useToggle,
  GetColorStatus,
  AccountStatus,
  SupportedCurrencies,
} from 'vopay';

import { FunctionComponent, useContext } from 'react';
import { Prompt, useParams, useLocation } from 'react-router-dom';

import AccountCredentialsPanel from './AccountCredentialsPanel';
import AccountOnboardingPanel from './AccountOnboardingPanel';
import AccountTabs from './AccountTabs';
import AccountContext from '../AccountContext';

import { usePendingData } from '@/providers/PendingData.provider';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';

import type { AccountsHandlers, Balances as Balance } from '../types';

type AccountProps = {
  hasValidOnboardingUrl: boolean | null;
  handlers: AccountsHandlers;
  onCopyClick: () => void;
  onActionClick: () => void;
  onBoardingLink: string | null;
};

type FC = FunctionComponent<AccountProps>;

const Account: FC = ({ hasValidOnboardingUrl, onCopyClick, onActionClick, onBoardingLink, handlers }: AccountProps) => {
  const { details, transactions } = useContext(AccountContext);
  const { hasPendingData } = usePendingData();

  const { canAccessOnboardingTokenPanel, canSeeAccountCredential } = useModuleFeatureFlag('accounts');

  const { accountId } = useParams<{ accountId: string }>();

  if (!details[accountId]) {
    return null;
  }
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const query = new URLSearchParams(useLocation().search);
  const balance: Balance = {
    account: query.get('account') || '0',
    pending: query.get('pending') || '0',
    available: query.get('available') || '0',
    immediate: query.get('immediate') || '0',
    reserve: query.get('reserve') || '0',
    currency: (query.get('currency') as SupportedCurrencies) || 'CAD',
    isActive: query.get('isActive') === 'true',
    accountStatus: query.get('accountStatus') || '',
    security: query.get('security') || '',
  };

  const [showCredentials, toggleCredentials] = useToggle(false);
  return (
    <>
      {Object.values(details).length && (
        <>
          {canAccessOnboardingTokenPanel && balance.accountStatus === 'Application Pending' && (
            <Box width="50em" jc="fe">
              <AccountOnboardingPanel
                hasValidOnboardingUrl={hasValidOnboardingUrl}
                onActionClick={onActionClick}
                onCopyClick={onCopyClick}
                onBoardingLink={onBoardingLink}
              />
            </Box>
          )}
          {canSeeAccountCredential && showCredentials && <AccountCredentialsPanel accountId={accountId} />}

          <Box flx="col">
            <Box gap="common" ai="center">
              <BackButton variant="arrow" backButtonEnabled size="sm" />
              <Title title={details[accountId].accountName} />
            </Box>

            <Box pt="md" pb="md" gap="sm">
              <Badge
                label={balance.isActive ? AccountStatus.Active : balance.accountStatus}
                color={balance.isActive ? 'positive' : GetColorStatus(balance.accountStatus)}
                hasIcon
              />
              {canSeeAccountCredential && (
                <Button
                  id="showHideAccountCredentials"
                  label={`${showCredentials ? 'Hide' : 'Show'} Credentials`}
                  color="primary"
                  size="sm"
                  isDisabled={isRoleViewOnly}
                  onClick={() => toggleCredentials()}
                />
              )}
            </Box>
          </Box>

          <AccountTabs transactions={transactions} handlers={handlers} />

          <Prompt when={hasPendingData} message="You have unsaved Account Permissions that will be discarded." />
        </>
      )}
    </>
  );
};

export default Account;
